import type { SVGProps } from "react";

/**
 * Iconos de trazo, dibujados a mano en SVG inline.
 *
 * El panel no carga ninguna librería de iconos por CDN a propósito (mismo
 * criterio que las fuentes, ver `index.css`): se despliega en un VPS propio y
 * una dependencia externa para algo tan simple es un punto de fallo evitable.
 * Todos comparten el mismo contrato (`stroke="currentColor"`, `viewBox`
 * 24×24) para poder colorearlos con las clases de texto de Tailwind.
 */

type IconProps = SVGProps<SVGSVGElement>;

function base(props: IconProps, children: React.ReactNode) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function IconDashboard(props: IconProps) {
  return base(
    props,
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.2" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.2" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.2" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.2" />
    </>,
  );
}

export function IconDocument(props: IconProps) {
  return base(
    props,
    <>
      <path d="M6 3h8l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h4" />
      <path d="M8 12h8M8 16h5" />
    </>,
  );
}

export function IconBuilding(props: IconProps) {
  return base(
    props,
    <>
      <path d="M4 21V6l7-3 7 3v15" />
      <path d="M4 21h16" />
      <path d="M9 9h1M14 9h1M9 13h1M14 13h1M9 21v-4h6v4" />
    </>,
  );
}

export function IconTruck(props: IconProps) {
  return base(
    props,
    <>
      <path d="M3 16V6a1 1 0 0 1 1-1h9v11" />
      <path d="M13 9h4l4 4v3h-2" />
      <circle cx="7" cy="18" r="1.8" />
      <circle cx="17" cy="18" r="1.8" />
      <path d="M9 18h6" />
    </>,
  );
}

export function IconChart(props: IconProps) {
  return base(
    props,
    <>
      <path d="M4 20V4M4 20h16" />
      <path d="M8 16v-4M12 16V8M16 16v-7" />
    </>,
  );
}

export function IconAudit(props: IconProps) {
  return base(
    props,
    <>
      <path d="M9 3h6l1 3H8l1-3z" />
      <path d="M6 6h12v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6z" />
      <path d="m9 12 2 2 4-4" />
    </>,
  );
}

export function IconPalette(props: IconProps) {
  return base(
    props,
    <>
      <path d="M12 3a9 9 0 1 0 0 18c1.5 0 2-1 2-2s-.5-1.5-.5-2.5S14 15 15 15h2a4 4 0 0 0 4-4c0-4.5-4-8-9-8z" />
      <circle cx="7.5" cy="10.5" r="1" />
      <circle cx="12" cy="7.5" r="1" />
      <circle cx="16.5" cy="10.5" r="1" />
    </>,
  );
}

export function IconImage(props: IconProps) {
  return base(
    props,
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 16-5-5-4 4-2-2-5 5" />
    </>,
  );
}

export function IconKey(props: IconProps) {
  return base(
    props,
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12 20 3M16 8l2.5 2.5M13 11l2 2" />
    </>,
  );
}

export function IconUsers(props: IconProps) {
  return base(
    props,
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="17" cy="8" r="2.4" />
      <path d="M15.5 14.2c2.6.5 4.5 2.7 4.5 5.8" />
    </>,
  );
}

export function IconSettings(props: IconProps) {
  return base(
    props,
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 13a7.7 7.7 0 0 0 0-2l1.9-1.5-2-3.4-2.2.7a7.6 7.6 0 0 0-1.7-1L15 3h-6l-.4 2.8a7.6 7.6 0 0 0-1.7 1l-2.2-.7-2 3.4L4.6 11a7.7 7.7 0 0 0 0 2l-1.9 1.5 2 3.4 2.2-.7c.5.4 1.1.8 1.7 1L9 21h6l.4-2.8c.6-.3 1.2-.6 1.7-1l2.2.7 2-3.4z" />
    </>,
  );
}

export function IconWarning(props: IconProps) {
  return base(
    props,
    <>
      <path d="M12 3 2 20h20L12 3z" />
      <path d="M12 10v4M12 17h.01" />
    </>,
  );
}

export function IconPulse(props: IconProps) {
  return base(
    props,
    <>
      <path d="M3 12h4l2 6 4-14 2 8h6" />
    </>,
  );
}

export function IconChecklist(props: IconProps) {
  return base(
    props,
    <>
      <path d="M9 5h11M9 12h11M9 19h11" />
      <path d="m3.5 4.5 1.2 1.2L6.5 3.7M3.5 11.5l1.2 1.2 1.8-2M3.5 18.5l1.2 1.2 1.8-2" />
    </>,
  );
}

export function IconSun(props: IconProps) {
  return base(
    props,
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M2 12h2M20 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4" />
    </>,
  );
}

export function IconMoon(props: IconProps) {
  return base(props, <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z" />);
}

export function IconSystem(props: IconProps) {
  return base(
    props,
    <>
      <rect x="3" y="4" width="18" height="12" rx="1.5" />
      <path d="M8 20h8M12 16v4" />
    </>,
  );
}

/** Rascacielos: gestión de tenants (corporaciones), distinto de `IconBuilding` (contrapartes). */
export function IconOffice(props: IconProps) {
  return base(
    props,
    <>
      <path d="M6 21V3.8a.8.8 0 0 1 .8-.8h6.4a.8.8 0 0 1 .8.8V21" />
      <path d="M14 21v-9.2a.8.8 0 0 1 .8-.8h3.4a.8.8 0 0 1 .8.8V21" />
      <path d="M3 21h18" />
      <path d="M8.5 6.5h1M11.5 6.5h1M8.5 10h1M11.5 10h1M8.5 13.5h1M11.5 13.5h1M15.5 14h1M15.5 17h1" />
    </>,
  );
}

export function IconPlus(props: IconProps) {
  return base(props, <path d="M12 5v14M5 12h14" />);
}

export function IconLogout(props: IconProps) {
  return base(
    props,
    <>
      <path d="M9 21H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h4" />
      <path d="M16 17l5-5-5-5M21 12H9" />
    </>,
  );
}

/** Base de datos con flecha circular: sección de backup y restauración. */
export function IconBackup(props: IconProps) {
  return base(
    props,
    <>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v4c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
      <path d="M4 10v4c0 1.66 3.58 3 8 3" />
      <path d="M18 17v-4M16 15l2 2 2-2" />
    </>,
  );
}

/** Cilindro S3: sección de almacenes de objeto. */
export function IconDatabase(props: IconProps) {
  return base(
    props,
    <>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v4c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
      <path d="M3 9v4c0 1.66 4.03 3 9 3s9-1.34 9-3V9" />
      <path d="M3 13v4c0 1.66 4.03 3 9 3s9-1.34 9-3v-4" />
    </>,
  );
}
